import {graphql} from 'react-relay';
import Util from '../forecast-app/shared/util/util';
import * as tracking from '../tracking';
import {trackAndCommitMutation} from '../tracking/amplitude/TrackMutation';

const mutation = graphql`
	mutation UpdateApprovalStatusMutation($input: UpdateApprovalStatusInput!, $weekStart: String, $weekEnd: String) {
		updateApprovalStatus(input: $input) {
			timeRegs {
				id
				approvalStatus
				noApprovalStatusChange
				lockedInPeriod
				# The date of every row the transition moved. SUBMIT_TIME_OFF sweeps
				# LocalDate.MIN..MAX by design (TimeApprovalLogic.handleSubmitTimeOffFetch), so
				# the caller cannot assume the rows it got back are the ones on screen — it has
				# to compare them against the viewed period before reporting what happened.
				year
				month
				day
			}
			persons {
				id
				submitLockedDateYear
				submitLockedDateMonth
				submitLockedDateDay
				# PIKA-519: pull back the affected person's week-level approval rows so
				# Relay's normalized cache learns about a new/updated person_week_approval
				# row (the 0h-week APPROVE/DECLINE/REVERSE_APPROVAL/RESUBMIT path). Without
				# this, the Time Approval page synthesis still sees an empty weekApprovals
				# array and falls through to lock-based step 3 — so the row stays on the
				# Submitted tab even though the backend wrote the APPROVED state correctly.
				# Args MUST match the parent query in TimeApprovalLogic.js's
				# ownedProjectPersonsQuery so Relay merges the response into the same
				# storage key. Caller passes weekStart/weekEnd as YYYY-MM-DD strings
				# matching currentViewingDate.startOf('week') / endOf('week').
				weekApprovals(startDate: $weekStart, endDate: $weekEnd) {
					id
					weekStartYear
					weekStartMonth
					weekStartDay
					approvalStatus
					declineReason
				}
			}
			errors
		}
	}
`;

function getOptimisticResponse(input) {
	// TODO: Maybe use optimisticTimeRegIds?
}

function commit(environment, input, onSuccess, onError) {
	// PIKA-519: weekStart/weekEnd are mutation-level args (for the weekApprovals
	// field on the persons response), NOT part of the UpdateApprovalStatusInput.
	// Callers (the Time Approval page) pass them on the input object alongside the
	// GraphQL input fields; we pluck them out here before forwarding the rest to
	// the $input variable. Other callers may omit them entirely — weekApprovals
	// then returns the unfiltered list, which Relay still merges by record id.
	const {weekStart, weekEnd, ...inputFields} = input;

	const changes = [];

	for (const key of Object.keys(inputFields)) {
		if (['personIds'].includes(key) || inputFields[key] === undefined) {
			continue;
		}
		changes.push(key);
	}
	tracking.trackEvent('Approval Status updated', {_Changed: changes});

	const variables = {
		input: {
			csrfToken: Util.getCsrfValue(),
			socketClientId: Util.getClientId(),
			...inputFields,
		},
		// MUST match the parent ownedProjectPersonsQuery's args in
		// TimeApprovalLogic.js so the returned weekApprovals records merge into the
		// same Relay cache slot.
		weekStart: weekStart ?? null,
		weekEnd: weekEnd ?? null,
	};

	return trackAndCommitMutation(environment, {
		mutation,
		variables,
		optimisticResponse: getOptimisticResponse(input),
		onCompleted: onSuccess,
		onError: onError,
	});
}

export default {commit};
